import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subject, takeUntil } from 'rxjs';
import { PackagingTypes } from 'src/app/private/services/packaging-types.service';
import { PackagingsService } from 'src/app/private/services/packagings.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { FinalProductDto } from 'src/app/shared/dto/final-product.dto';
import { PackagingTypeDto } from 'src/app/shared/dto/packaging-type.dto';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';

@Component({
    selector: 'app-modal-select-packaging-type',
    standalone: true,
    imports: [
        NzSelectModule,
        NzFormModule,
        ReactiveFormsModule,
        FormsModule,
        NzModalTitleDirective,
        NzModalFooterDirective,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './modal-select-packaging-type.component.html',
    styleUrl: './modal-select-packaging-type.component.scss'
})
export class ModalSelectPackagingTypeComponent implements OnInit, OnDestroy {

    isLoading: boolean = true;
    isSaving: boolean = false;
    
    packagingTypes: PackagingTypeDto[] = [];
    finalProduct: FinalProductDto = new FinalProductDto();
    form: FormGroup;

    readonly data = inject(NZ_MODAL_DATA);
    private readonly modalRef = inject(NzModalRef);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly packagingsService = inject(PackagingsService);
    private readonly packagingTypesService = inject(PackagingTypes);
    private readonly message = inject(NzMessageService);
    private readonly unsubscribe$ = new Subject<void>();

    constructor(){
        this.form = this.fb.group({
            packagingTypeSelected: ['', [Validators.required]]
        });
    }

    async ngOnInit(): Promise<void> {
        this.finalProduct = this.data?.finalProduct;
        if (!this.finalProduct.id) {
            this.message.error('Error al obtener el producto final');
        }

        await this.packagingTypesService.setupWebSocket();
        this.packagingTypesService.packagingTypes$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (packagingTypes: PackagingTypeDto[]) => {
                    this.packagingTypes = packagingTypes;
                },
                error: () => {
                    this.message.error('Error al obtener los tipos de formato');
                }
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.packagingTypesService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    async handleSave() {
        try {
            this.isSaving = true;

            if(!this.form.valid){
                this.isSaving = false;
                this.message.error('Debe seleccionar un tipo de formato');
                return;
            }
            const packagingTypeSelected = this.form.get('packagingTypeSelected')?.value;

            const packagingToCreate: PackagingDto = {
                articleId: this.finalProduct.article!.id,
                packagingTypeId: packagingTypeSelected.id!
            };

            const packagingCreated = await this.packagingsService.create(packagingToCreate);
            await this.packagingsService.assignPackagingsToFinalProduct([packagingCreated.id!], this.finalProduct.id!);
            
            if(this.finalProduct.articleBatch){
                await this.packagingsService.assignPackagingsToArticleBatch([packagingCreated.id!], this.finalProduct.articleBatch.id!);
            } else {
                await this.packagingsService.assignPackagingsToArticle([packagingCreated.id!], this.finalProduct.article!.id);
            }

            this.isSaving = false;
            this.modalRef.close(packagingCreated);
        } catch (e: any) {
            this.isSaving = false;
            this.message.error(e.message);
        }
    }

    async handleCancel() {
        this.modalRef.close();
    }
}
